/** Parse a hex color string (3- or 6-digit) into an RGB object. */
export function parseHex(value: string | undefined): { r: number; g: number; b: number } | null {
  if (!value) return null;
  const raw = value.trim().replace('#', '');
  const hex = raw.length === 3 ? raw.split('').map((segment) => `${segment}${segment}`).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

/** Blend a base hex color with a tint hex color at a given mix ratio, output as rgba(). */
export function blendColor(base: string, tint: string | undefined, mix: number, alpha: number): string {
  const baseRgb = parseHex(base);
  const tintRgb = parseHex(tint);
  if (!baseRgb) return base;
  if (!tintRgb) return `rgba(${baseRgb.r}, ${baseRgb.g}, ${baseRgb.b}, ${alpha})`;
  const factor = Math.max(0, Math.min(1, mix));
  const r = Math.round(baseRgb.r * (1 - factor) + tintRgb.r * factor);
  const g = Math.round(baseRgb.g * (1 - factor) + tintRgb.g * factor);
  const b = Math.round(baseRgb.b * (1 - factor) + tintRgb.b * factor);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Convert a hex color to rgba() with a given alpha. */
export function toRgba(color: string, alpha: number): string {
  const raw = color.trim().replace('#', '');
  const hex = raw.length === 3 ? raw.split('').map((segment) => `${segment}${segment}`).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Brighten a hex color by mixing it toward white by the given amount (0–1). */
export function brightenColor(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, (n >> 16) + Math.round((255 - (n >> 16)) * amount));
  const g = Math.min(255, ((n >> 8) & 0xff) + Math.round((255 - ((n >> 8) & 0xff)) * amount));
  const b = Math.min(255, (n & 0xff) + Math.round((255 - (n & 0xff)) * amount));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Darken a hex color by the given amount (0–255 per channel). */
export function darkenHex(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const r = Math.max(0, rgb.r - amount);
  const g = Math.max(0, rgb.g - amount);
  const b = Math.max(0, rgb.b - amount);
  const toHex = (v: number) => v.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Convert HSL (h: 0–360, s/l: 0–100) to a `#rrggbb` hex string. */
export function hslToHex(h: number, s: number, l: number): string {
  const sat = s / 100;
  const light = l / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = light - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (value: number) => Math.round((value + m) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Pick the next sequence-participant color: prefer an unused entry from the curated
 * palette `paletteColors`, then fall back to a deterministic golden-angle HSL spin
 * when the palette is exhausted.
 */
export function getNextParticipantColor(
  usedColors: ReadonlyArray<string>,
  paletteColors: ReadonlyArray<string>,
): string {
  const used = new Set(usedColors.map((c) => c.toLowerCase()));
  const paletteColor = paletteColors.find((color) => !used.has(color.toLowerCase()));
  if (paletteColor) return paletteColor;

  for (let idx = 0; idx < 360; idx += 1) {
    const hue = Math.round((usedColors.length * 137.508 + idx * 17) % 360);
    const generated = hslToHex(hue, 72, 56).toLowerCase();
    if (!used.has(generated)) return generated;
  }

  return `#${Math.random().toString(16).slice(2, 8).padEnd(6, '0').slice(0, 6)}`;
}

/** Mix two hex colors and return hex, so the result can be brightened again. */
export function mixHex(base: string, tint: string, amount: number): string {
  const a = parseHex(base);
  const b = parseHex(tint);
  if (!a || !b) return base;
  const t = Math.max(0, Math.min(1, amount));
  const toHex = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return `#${toHex(a.r * (1 - t) + b.r * t)}${toHex(a.g * (1 - t) + b.g * t)}${toHex(a.b * (1 - t) + b.b * t)}`;
}
